/**
 * App — оболонка застосунку.
 *
 * Два головні розділи, як у ТЗ:
 *   • «Картки»           — повторення та перегляд усіх карток;
 *   • «Конспекти»        — конспекти + генерація карток через Gemini.
 *
 * Налаштування — модальне вікно (жодного зайвого таба), статистика — у шапці.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  BookOpen,
  GraduationCap,
  KeyRound,
  Layers,
  Settings as SettingsIcon,
  Sparkles,
} from 'lucide-react'

import KnowledgeBase from './components/KnowledgeBase.jsx'
import CardBrowser from './components/CardBrowser.jsx'
import ReviewSession from './components/ReviewSession.jsx'
import Settings from './components/Settings.jsx'
import { Modal, Loading, Alert, Badge } from './components/ui.jsx'
import { api } from './lib/api.js'
import { getSettings } from './lib/settings.js'
import { SUBJECTS } from './lib/constants.js'

const TABS = [
  { id: 'cards', label: 'Картки', icon: Layers, caption: 'Повторення та перегляд карток' },
  { id: 'knowledge', label: 'Конспекти', icon: BookOpen, caption: 'Конспекти та генерація' },
]

export default function App() {
  const [tab, setTab] = useState('cards')
  const [subView, setSubView] = useState('review') // 'review' | 'browse' на вкладці карток
  const [settings, setSettings] = useState(() => getSettings())
  const [showSettings, setShowSettings] = useState(false)
  const [decks, setDecks] = useState([])
  const [stats, setStats] = useState(null)
  const [subject, setSubject] = useState('all')
  const [bootError, setBootError] = useState(null)
  const [booting, setBooting] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.listDecks(), api.stats()])
      setDecks(d)
      setStats(s)
      setBootError(null)
    } catch (err) {
      setBootError(err?.message ?? 'Не вдалося зв’язатися з базою.')
    } finally {
      setBooting(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Тема з налаштувань — на весь документ
  useEffect(() => {
    document.documentElement.classList.toggle('dark', settings.theme === 'dark')
    document.documentElement.dataset.theme = settings.theme
  }, [settings.theme])

  // Дані з /api/stats: { summary, bySubject, byTag, activity, dueSoon }
  const summary = stats?.summary ?? null
  const totalCards = summary?.total ?? 0

  const dueNow = summary?.dueNow ?? 0

  if (booting) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loading label="Підключаюсь до бази…" />
      </div>
    )
  }

  const currentTab = TABS.find((item) => item.id === tab)

  return (
    <div className="app-shell min-h-dvh bg-[#090909] text-slate-100 lg:flex">
      <aside className="app-sidebar hidden lg:flex">
        <div className="app-brand"><span><GraduationCap className="h-5 w-5" /></span><div><b>НМТ</b><small>підготовка</small></div></div>
        <nav className="app-nav" aria-label="Основна навігація">
          <p>Розділи</p>
          {TABS.map((item) => <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`nav-primary ${tab === item.id ? 'is-active' : ''}`} aria-current={tab === item.id ? 'page' : undefined}>
            <item.icon className="h-5 w-5" /><span>{item.label}</span>{item.id === 'cards' && dueNow > 0 && <em>{dueNow}</em>}
          </button>)}
        </nav>
        <div className="sidebar-due"><div className="sidebar-due-count"><span>До повторення</span><b>{dueNow}</b></div><p>карток чекають на повторення</p><button type="button" onClick={() => { setTab('cards'); setSubView('review') }} className="btn-outline sidebar-cta">Почати сесію <Sparkles className="h-3.5 w-3.5" /></button></div>
        <button type="button" onClick={() => setShowSettings(true)} className="sidebar-settings"><SettingsIcon className="h-4 w-4" /> Налаштування{!settings.geminiKeys.length && <KeyRound className="ml-auto h-4 w-4" />}</button>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="mobile-topbar lg:hidden"><div className="app-brand"><span><GraduationCap className="h-5 w-5" /></span><b>НМТ</b></div><button type="button" onClick={() => setShowSettings(true)} className="btn-ghost !px-2"><SettingsIcon className="h-5 w-5" /></button></header>
        <main className="app-main">
          <section className="page-header">
            <div><p className="eyebrow">{currentTab?.caption}</p><h1>{tab === 'knowledge' ? 'Навчальний простір' : subView === 'review' ? 'Фокус на повторенні' : 'Усі картки'}</h1></div>
            <div className="page-header-actions">
              <label className="subject-picker"><span>Предмет</span><select value={subject} onChange={(e) => setSubject(e.target.value)}><option value="all">Усі предмети</option>{SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></label>
              {summary && <div className="header-metric"><b>{totalCards}</b><span>карток у базі</span></div>}
            </div>
          </section>

      {/* ── Помилка бази ── */}
      {bootError && (
        <div className="mb-5">
          <Alert tone="error" title="Немає зв’язку з D1">
            {bootError}
            <span className="mt-1 block text-sm">
              Локально: <code className="font-mono">npm run db:init</code>, потім{' '}
              <code className="font-mono">npm run cf:dev</code>.
            </span>
          </Alert>
        </div>
      )}

      {/* ── Вкладка «База знань» ── */}
      {tab === 'knowledge' && (
        <section>
          <KnowledgeBase
            settings={settings}
            decks={decks}
            onNeedSettings={() => setShowSettings(true)}
            onCardsAdded={refresh}
            dueNow={dueNow}
            onStartReview={() => { setTab('cards'); setSubView('review') }}
          />
        </section>
      )}

      {/* ── Вкладка «Картки і Повторення» ── */}
      {tab === 'cards' && (
        <section>
          <div className="view-switcher">
            {[
              { id: 'review', label: 'Повторення', icon: Sparkles },
              { id: 'browse', label: 'Усі картки', icon: Layers },
            ].map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setSubView(v.id)}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
                  subView === v.id
                    ? 'border-b-2 border-white text-white'
                    : 'text-slate-500 hover:text-slate-200'
                }`}
              >
                <v.icon className="h-4 w-4" />
                {v.label}
                {v.id === 'review' && dueNow > 0 && (
                  <Badge className="!bg-white !text-black">{dueNow}</Badge>
                )}
              </button>
            ))}
          </div>

          {subView === 'review' ? (
            <ReviewSession
              settings={settings}
              decks={decks}
              subject={subject}
              onFinished={refresh}
              onNeedCards={() => setTab('knowledge')}
            />
          ) : (
            <CardBrowser settings={settings} decks={decks} subject={subject} onChanged={refresh} />
          )}
        </section>
      )}

      {/* ── Налаштування ── */}
        </main>
      </div>

      <nav className="mobile-nav lg:hidden" aria-label="Основна навігація">{TABS.map((item) => <button key={item.id} type="button" onClick={() => setTab(item.id)} className={tab === item.id ? 'is-active' : ''} aria-current={tab === item.id ? 'page' : undefined}><item.icon className="h-5 w-5" /><span>{item.label}</span>{item.id === 'cards' && dueNow > 0 && <em aria-label={`${dueNow} карток до повторення`}>{dueNow}</em>}</button>)}<button type="button" onClick={() => setShowSettings(true)}><SettingsIcon className="h-5 w-5" /><span>Ще</span></button></nav>

      {showSettings && (
        <Modal open onClose={() => setShowSettings(false)} size="xl" title="Налаштування">
          <Settings
            onClose={() => setShowSettings(false)}
            onChanged={setSettings}
            onKeysChanged={() => {
              setSettings(getSettings())
              refresh()
            }}
          />
        </Modal>
      )}
    </div>
  )
}
